import { Route, Routes, ScrollToTop } from '@thd-olt-component-react/router';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useEffect } from 'react';
import { useAppContext } from './hooks/useContexts';
import { Error } from './pages/Error';
import { getRoutes } from './routes.js';
import {
  redirectGuestOrderTracking,
  redirecToErrorPage,
  redirectToSignIn,
} from './utils/http-helper';
import './styles/app.scss';

interface AppProps {
  customerType: 'b2b' | 'b2c';
  customerRole: string;
  serverPages: Record<string, unknown>;
}

export const App = ({ customerType, customerRole, serverPages }: AppProps) => {
  const { instance } = useAppContext();
  const { isLoggedIn, isB2CCustomer } = useThdCustomer();
  const { currentRouteName, currentRouteComponent } = instance.router;
  const routes = getRoutes(
    currentRouteName,
    currentRouteComponent,
    serverPages,
  );

  // Flag that stops redirect to login for selected pages.
  const routeToSigninExemptFlag = ['Home', 'GuestOrderTracking'].includes(
    currentRouteName,
  );
  const routeCancelItems = ['CancelItems'].includes(currentRouteName);

  useEffect(() => {
    // Redirects to guest order tracking if user is not logged in and trying to access cancel items page
    if (!isLoggedIn && routeCancelItems) {
      redirectGuestOrderTracking();
      return;
    }
    // Check's if user is logged in and has the svoc and user Id's
    if (!isLoggedIn && !routeToSigninExemptFlag) {
      redirectToSignIn();
    }

    // Checks if user is b2c and if requested page is a b2b only page
    if (
      isB2CCustomer &&
      ['BusinessTools', 'LinkedAccounts', 'Downloads'].includes(
        currentRouteName,
      )
    ) {
      redirecToErrorPage();
    }
  }, [isLoggedIn, isB2CCustomer, currentRouteName]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          exact
          path={routes.purchaseHistory.path}
          element={
            <routes.purchaseHistory.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.orderDetails.path}
          element={
            <routes.orderDetails.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.cancelItems.path}
          element={
            <routes.cancelItems.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.linkedAccounts.path}
          element={
            <routes.linkedAccounts.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.downloads.path}
          element={
            <routes.downloads.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.businessTools.path}
          element={
            <routes.businessTools.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.subscriptions.path}
          element={
            <routes.subscriptions.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.guestOrderTracking.path}
          element={
            <routes.guestOrderTracking.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.searchResults.path}
          element={
            <routes.searchResults.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.home.path}
          element={<routes.home.component />}
        />
        <Route path="/error" element={<Error />} />
        <Route element={<Error />} />
      </Routes>
    </>
  );
};

App.propTypes = {};
App.defaultProps = {};
